// Feedbacksounds voor Gamification & Notifications — gesynthetiseerd met de Web
// Audio API. Geen audiobestanden: geen licenties, geen downloads, en de duur ligt
// exact vast (zie SOUNDS: elke noot staat met start en uitsterftijd beschreven, en
// sounds.test.js bewaakt dat geen enkele sound langer dan 1,5 s duurt).
//
// Karakter: zachte sinus-tonen met een paar boventonen die snel uitsterven — klinkt
// als een gedempte bel of marimba. Bewust géén kassa, munten of fanfare.
//
// Harde regels (niet instelbaar):
//  - alleen in een ZICHTBAAR tabblad;
//  - alleen nadat de gebruiker al iets aangeklikt heeft (autoplay-beleid van de
//    browser). Kan het niet meteen, dan vervalt het geluid: er is geen wachtrij;
//  - minstens 20 s tussen twee sounds, over alle open tabbladen heen (localStorage);
//  - één centrale volumeknop + limiter.

export const MAX_DUUR = 1.5;
export const MIN_TUSSENTIJD_MS = 20000;

const C5 = 523.25; const E5 = 659.25; const G5 = 783.99; const C6 = 1046.5; const E6 = 1318.51; const G4 = 392.0; const D5 = 587.33;

// noot: f = frequentie, t = start (s), d = uitsterftijd (s), g = relatieve sterkte.
// `naklank` = zachte echo (alleen bij de mijlpaal). `glans` = hoge, zeer zachte
// toon met een lichte trilling bovenop (alleen bij reward).
export const SOUNDS = {
  sale: { label: 'Sale', noten: [{ f: G4, t: 0, d: 0.22, g: 0.9 }, { f: D5, t: 0.11, d: 0.26, g: 1 }] },
  klant: { label: 'Nieuwe klant', noten: [{ f: G5, t: 0, d: 0.42, g: 1 }, { f: G5 * 1.5, t: 0.01, d: 0.3, g: 0.22 }] },
  reward: { label: 'Reward', noten: [{ f: C5, t: 0, d: 0.5, g: 0.9 }, { f: G5, t: 0.07, d: 0.5, g: 0.8 }, { f: E6, t: 0.14, d: 0.52, g: 0.7 }], glans: { f: C6 * 2, t: 0.14, d: 0.5, g: 0.1 } },
  prestatie: { label: 'Prestatie', noten: [{ f: C5, t: 0, d: 0.3, g: 0.9 }, { f: E5, t: 0.12, d: 0.3, g: 0.9 }, { f: G5, t: 0.24, d: 0.42, g: 1 }] },
  mijlpaal: { label: 'Mijlpaal', noten: [{ f: C5, t: 0, d: 0.35, g: 0.85 }, { f: E5, t: 0.13, d: 0.35, g: 0.85 }, { f: G5, t: 0.26, d: 0.4, g: 0.9 }, { f: C6, t: 0.39, d: 0.7, g: 1 }], naklank: true },
};

// Totale duur van een sound in seconden (laatste noot + uitsterven + naklank).
export function duurVan(naam) {
  const s = SOUNDS[naam];
  if (!s) return 0;
  const alle = [...s.noten, ...(s.glans ? [s.glans] : [])];
  const einde = Math.max(...alle.map((n) => n.t + n.d));
  return einde + (s.naklank ? 0.18 : 0);
}

let ctx = null;
let uitgang = null;
let ontgrendeld = false;

function maakContext() {
  if (ctx) return ctx;
  const AC = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
  if (!AC) return null;
  ctx = new AC();
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -10; limiter.knee.value = 6; limiter.ratio.value = 12; limiter.attack.value = 0.003; limiter.release.value = 0.1;
  uitgang = ctx.createGain();
  uitgang.connect(limiter);
  limiter.connect(ctx.destination);
  return ctx;
}

// Aanroepen bij de eerste klik/toets: pas dan mag een browser geluid maken.
export function ontgrendelAudio() {
  ontgrendeld = true;
  const c = maakContext();
  if (c && c.state === 'suspended') c.resume().catch(() => {});
}
export const audioOntgrendeld = () => ontgrendeld;

function speelNoot(c, doel, n, isGlans) {
  const start = c.currentTime + 0.02 + n.t;
  const env = c.createGain();
  env.gain.setValueAtTime(0.0001, start);
  env.gain.exponentialRampToValueAtTime(Math.max(0.0002, n.g), start + 0.012);
  env.gain.exponentialRampToValueAtTime(0.0001, start + n.d);
  env.connect(doel);
  // Grondtoon + twee boventonen die sneller uitsterven = gedempte bel.
  const delen = isGlans ? [[1, 1]] : [[1, 1], [2, 0.18], [4, 0.06]];
  for (const [veelvoud, sterkte] of delen) {
    const osc = c.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = n.f * veelvoud;
    const g = c.createGain();
    g.gain.setValueAtTime(sterkte, start);
    if (veelvoud > 1) g.gain.exponentialRampToValueAtTime(0.0001, start + n.d * 0.45);
    osc.connect(g); g.connect(env);
    if (isGlans) {
      const tril = c.createOscillator(); const diepte = c.createGain();
      tril.frequency.value = 7; diepte.gain.value = 6;
      tril.connect(diepte); diepte.connect(osc.frequency);
      tril.start(start); tril.stop(start + n.d + 0.05);
    }
    osc.start(start); osc.stop(start + n.d + 0.05);
  }
}

// Speelt een sound. `volume` 0–100. `opties.voorbeeld` = de ▶-knop in de
// instellingen: slaat de tussentijd over (de gebruiker vraagt er zelf om).
// Geeft true terug als er effectief geluid is gestart.
export function speel(naam, volume = 40, opties = {}) {
  const s = SOUNDS[naam];
  if (!s || !(volume > 0)) return false;
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return false;
  if (!ontgrendeld) return false;
  if (!opties.voorbeeld) {
    try {
      const vorige = Number(localStorage.getItem('gami.laatsteSound')) || 0;
      if (Date.now() - vorige < MIN_TUSSENTIJD_MS) return false;
      localStorage.setItem('gami.laatsteSound', String(Date.now()));
    } catch (_) { /* geen opslag: dan zonder tussentijd over tabbladen heen */ }
  }
  const c = maakContext();
  if (!c) return false;
  if (c.state === 'suspended') { c.resume().catch(() => {}); if (c.state === 'suspended') return false; }
  // Kwadratisch: een schuif op 50 % klinkt dan ook als "half zo luid".
  const v = Math.min(1, Math.max(0, volume / 100));
  uitgang.gain.setValueAtTime(v * v * 0.5, c.currentTime);
  let doel = uitgang;
  if (s.naklank) {
    const droog = c.createGain(); droog.connect(uitgang);
    const echo = c.createDelay(0.5); echo.delayTime.value = 0.16;
    const echoSterkte = c.createGain(); echoSterkte.gain.value = 0.22;
    droog.connect(echo); echo.connect(echoSterkte); echoSterkte.connect(uitgang);
    doel = droog;
  }
  for (const n of s.noten) speelNoot(c, doel, n, false);
  if (s.glans) speelNoot(c, doel, s.glans, true);
  return true;
}
